import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Type } from '@angular/core';
import { MessageContainerComponent } from './message-container.component';
import { MessageData, MessageDataFilled, MessageDataOptions } from './message.definitions';

// TODO: remove MessageData generic type as it has no contributon in typing
export class MessageBaseService<ContainerClass extends MessageContainerComponent, MessageData> {
  protected _counter = 0; // Id counter for messages
  protected _container: ContainerClass;

  private _messageQueue: {message: object, options?: MessageDataOptions}[] = [];
  private _containerReady = false;

  constructor(overlay: Overlay, containerClass: Type<ContainerClass>, private _idPrefix: string = '') {
    // Wait for wrapper to get init parameters, but do not rely on arbitrary timeout
    Promise.resolve().then(() => {
      this._container = overlay.create().attach(new ComponentPortal(containerClass)).instance;
      this._containerReady = true;
      // Flush queued messages
      this._messageQueue.forEach(({message, options}) => this.createMessage(message, options));
      this._messageQueue = [];
    });
  }

  remove(messageId?: string): void {
    if (messageId) {
      this._container.removeMessage(messageId);
    } else {
      this._container.removeMessageAll();
    }
  }

  createMessage(message: object, options?: MessageDataOptions): MessageDataFilled {
    const resultMessage: MessageDataFilled = {
      ...message,
      ...{
        messageId: this._generateMessageId(),
        options,
        createdAt: new Date()
      }
    };
    if (!this._containerReady) {
      this._messageQueue.push({message, options});
      return resultMessage;
    }
    this._container.createMessage(resultMessage);
    return resultMessage;
  }

  protected _generateMessageId(): string {
    return this._idPrefix + this._counter++;
  }
}

@Injectable()
export class MessageService extends MessageBaseService<MessageContainerComponent, MessageData> {
  constructor(overlay: Overlay) {
    super(overlay, MessageContainerComponent, 'message-');
  }

  // Shortcut methods
  success(content: string, options?: MessageDataOptions): MessageDataFilled {
    return this.create('success', content, options);
  }

  error(content: string, options?: MessageDataOptions): MessageDataFilled {
    return this.create('error', content, options);
  }

  info(content: string, options?: MessageDataOptions): MessageDataFilled {
    return this.create('info', content, options);
  }

  warning(content: string, options?: MessageDataOptions): MessageDataFilled {
    return this.create('warning', content, options);
  }

  create(type: string, content: string, options?: MessageDataOptions): MessageDataFilled {
    // If type is 'error', set default Duration to 15000 unless overridden
    const mergedOptions = type === 'error' ? { Duration: 15000, ...options } : options;
    return this.createMessage({ type, content }, mergedOptions);
  }
}
